/*
  Заведение суда в справочнике: участок мирового судьи или районный суд.

  🔴 Поля зависят от вида (14.09.2026, указание владельца - заводить суды в том
  же разделе). У участка обязательны номер и ФИО судьи: оба печатаются в шапку
  заявления о вынесении приказа. У районного суда номера нет вовсе, а судью
  назначают по делу - показать эти поля значило бы предложить человеку выдумать
  данные, которые уедут в шапку искового заявления.

  Вид выбирается переключателем, а не выпадающим списком: вариантов два, и оба
  видны сразу. Список из двух строк прячет половину выбора за нажатием.

  Начальный вид приходит пропсом с 14.09.2026: кнопка заведения стоит внутри
  вкладки справочника, и форма, открытая с вкладки районных судов, обязана
  открыться районным судом. Переключатель при этом остаётся - передумать можно,
  не закрывая окна.
*/

import { useState } from 'react'

import { Field, Notice } from './Common.jsx'
import InfoHint from './InfoHint.jsx'
import Dropdown from './Select.jsx'

const BLANK = { number: '', name: '', court_address: '', judge_name: '' }

const MAGISTRATE = 'magistrate'
const DISTRICT = 'district'

export default function CourtForm({ initialKind, districts, busy, error, onCancel, onCreate }) {
  const [kind, setKind] = useState(initialKind ?? MAGISTRATE)
  const [form, setForm] = useState(BLANK)
  const [districtId, setDistrictId] = useState('')

  const magistrate = kind === MAGISTRATE
  const change = (field) => (event) => setForm({ ...form, [field]: event.target.value })

  /*
    Обязательное у участка и у района разное - ровно то же правило, что на
    сервере (`_requisites` в `api/court_sections.py`). Здесь оно только гасит
    кнопку: решает сервер, и второй экземпляр правила здесь не заводится - он
    разошёлся бы с первым.
  */
  const filled = magistrate
    ? form.number.trim() && form.name.trim() && form.court_address.trim() && form.judge_name.trim()
    : form.name.trim() && form.court_address.trim()

  async function submit(event) {
    event.preventDefault()
    const done = await onCreate({
      ...form,
      kind,
      district_court_id: magistrate && districtId ? districtId : null,
    })
    if (done) {
      setForm(BLANK)
      setDistrictId('')
    }
  }

  return (
    <form onSubmit={submit}>
        <div className="row row--choices" role="radiogroup" aria-label="Что заводим">
          <label className="choice">
            <input
              type="radio"
              name="court-kind"
              checked={magistrate}
              onChange={() => setKind(MAGISTRATE)}
            />
            Судебный участок мирового судьи
          </label>
          <label className="choice">
            <input
              type="radio"
              name="court-kind"
              checked={!magistrate}
              onChange={() => setKind(DISTRICT)}
            />
            Районный суд
          </label>
        </div>

        {magistrate ? (
          <div className="row row--fields">
            <Field label="Номер участка" width="xs">
              <input
                className="field__input"
                value={form.number}
                onChange={change('number')}
                placeholder="5"
              />
            </Field>
            <Field label="ФИО мирового судьи" width="m">
              <input
                className="field__input"
                value={form.judge_name}
                onChange={change('judge_name')}
                placeholder="Петрова Анна Сергеевна"
              />
            </Field>
          </div>
        ) : null}

        <Field label="Наименование суда" width="l">
          <input
            className="field__input"
            value={form.name}
            onChange={change('name')}
            placeholder={
              magistrate
                ? 'Мировому судье судебного участка № 5 Центрального района г. Кемерово'
                : 'В Центральный районный суд г. Кемерово'
            }
          />
        </Field>
        <Field
          label="Адрес суда"
          width="l"
          hint="Наименование уходит в шапку заявления в том виде, в каком вы его впишете."
        >
          <input
            className="field__input"
            value={form.court_address}
            onChange={change('court_address')}
            placeholder="650000, г. Кемерово, ул. Весенняя, 22"
          />
        </Field>

        {/*
          Районный суд у участка - адресат иска по его домам. Своей привязки
          домов у района нет: участок целиком входит в один судебный район,
          поэтому район известен, как только известен участок.
        */}
        {magistrate ? (
          <div className="field field--l field--hinted">
            {/* Своя подпись, а не `Field`: разметка выпадающего списка - кнопка,
                и обёртка `<label>` её не называет; читалке имя даётся ссылкой
                (правило 6в скилла). Пояснение - под значком «i», как у
                остальных полей (`Field`, 02.10.2026). */}
            <span className="field__label" id="district-court-label">
              Районный суд
            </span>
            <InfoHint text="Адресат иска по домам этого участка. Можно указать позже." />
            <Dropdown
              value={districtId}
              onChange={setDistrictId}
              options={Object.fromEntries(districts.map((court) => [court.id, court.name]))}
              empty="не указан"
              labelledBy="district-court-label"
            />
          </div>
        ) : null}

      {/* Отказ стоит НАД кнопками, внутри того же окна: под ними его пришлось бы
          искать прокруткой, а окно короткое (чек-лист формы, пункт 1). */}
      <Notice error={error} />

      <div className="row row--tight">
        <button className="button" type="submit" disabled={busy || !filled}>
          {magistrate ? 'Завести участок' : 'Завести районный суд'}
        </button>
        <button className="button button--quiet" type="button" onClick={onCancel} disabled={busy}>
          Отмена
        </button>
      </div>
    </form>
  )
}
